import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Youtube, Save } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

export default function HomeSettingsPanel() {
  const [url, setUrl] = useState("");
  const [embed, setEmbed] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api("/admin/home-settings")
      .then((r) => { setUrl(r.video_url || ""); setEmbed(r.embed_url || ""); })
      .catch(() => {})
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  async function save() {
    if (!url.trim()) return toast.error("Saisissez une URL YouTube.");
    setSaving(true);
    try {
      const r = await api("/admin/home-settings", { method: "PUT", body: { video_url: url.trim() } });
      setUrl(r.video_url); setEmbed(r.embed_url);
      toast.success("Vidéo de la page d'accueil mise à jour.");
    } catch (e) {
      toast.error(e.message || "URL YouTube invalide.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-6 max-w-2xl" data-testid="home-settings-panel">
      <div className="flex items-center gap-2 mb-1">
        <Youtube size={18} className="text-findo-red" />
        <h2 className="font-display text-lg font-semibold text-findo-navy">Paramètres de la page d'accueil</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Modifiez l'URL de la vidéo de présentation affichée sur la page d'accueil. Collez n'importe quel
        lien YouTube (watch, youtu.be, shorts…) — la vidéo se met à jour automatiquement.
      </p>

      <Label htmlFor="home-video-url" className="text-sm font-semibold text-findo-navy">URL de la vidéo YouTube</Label>
      <div className="mt-2 flex flex-col sm:flex-row gap-2">
        <Input
          id="home-video-url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.youtube.com/watch?v=..."
          disabled={loading}
          className="rounded-xl"
          data-testid="home-video-url-input"
        />
        <Button onClick={save} disabled={saving || loading} className="rounded-xl font-semibold shrink-0" data-testid="home-video-save-btn">
          <Save size={15} className="mr-1.5" /> {saving ? <span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Enregistrement…</span> : "Enregistrer"}
        </Button>
      </div>

      {embed && (
        <div className="mt-6">
          <p className="text-xs font-semibold text-slate-500 mb-2">Aperçu</p>
          <div className="relative w-full aspect-video overflow-hidden rounded-xl border border-border bg-black" data-testid="home-video-preview">
            <iframe
              key={embed}
              className="absolute inset-0 h-full w-full"
              src={embed}
              title="Aperçu vidéo d'accueil"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
}
